import CloseOutlined from '@ant-design/icons/CloseOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import {
  AutoComplete,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Typography,
} from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import {
  RugReportSectionId,
  SectionAddendum,
} from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { ReportSection } from 'pages/reporting/shared/types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { autocompleteFilterOption } from 'utils/fde/antD'

const { Title, Text } = Typography

type SectionAddendumsFormData = {
  sectionAddendums: SectionAddendum[]
}

const RenderReact: React.FC = () => {
  const { formData, report, handleSectionAddendumsFinish } = useRugReportOps()
  const [form] = Form.useForm<SectionAddendumsFormData>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (formData?.sectionAddendums) {
      form.setFieldsValue({
        sectionAddendums: formData.sectionAddendums,
      })
    }
  }, [form, formData?.sectionAddendums])

  const sectionOptions = useMemo(() => {
    const sections = report.sections.map((section) => ({
      value: section.id,
      label: section.title,
    }))
    return sections
  }, [report.sections])

  const onFinish = useCallback(
    async (values: SectionAddendumsFormData) => {
      setIsSubmitting(true)
      try {
        await handleSectionAddendumsFinish(values.sectionAddendums || [])
      } finally {
        setIsSubmitting(false)
      }
    },
    [handleSectionAddendumsFinish]
  )

  return (
    <div style={{ padding: '24px', maxWidth: '1200px' }}>
      <Title level={2}>Section Addendums</Title>
      <Text type='secondary'>
        Add additional blank pages to select sections. You can choose from
        existing sections or create new custom sections like
        &quot;Certifications&quot; or &quot;Drawings&quot;.
      </Text>

      <div style={{ marginTop: '24px' }}>
        <StyledForm form={form} layout='vertical' onFinish={onFinish}>
          <Form.List name='sectionAddendums'>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ marginBottom: 16 }}>
                    <Card
                      size='small'
                      title={`Addendum ${name + 1}`}
                      extra={
                        <CloseOutlined
                          onClick={() => {
                            remove(name)
                          }}
                        />
                      }>
                      <Form.Item
                        {...restField}
                        name={[name, 'sectionId']}
                        label='Section'
                        rules={[
                          { required: true, message: 'Missing section' },
                        ]}>
                        <AutoComplete
                          options={sectionOptions}
                          placeholder='Select existing section or type new custom section name'
                          filterOption={autocompleteFilterOption}
                          allowClear
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'pageSubtitle']}
                        label='Subtitle for Pages'>
                        <Input placeholder='Optional subtitle to appear on pages' />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'addedPageCount']}
                        label='Number of Additional Pages'
                        rules={[
                          {
                            required: true,
                            message: 'Please specify number of pages',
                          },
                        ]}>
                        <InputNumber min={1} precision={0} />
                      </Form.Item>
                    </Card>
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type='dashed'
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}>
                    Add Section Addendum
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item>
            <CantileverButton
              primary
              onClick={() => form.submit()}
              disabled={isSubmitting}
              style={{ width: '100%' }}>
              Save Section Addendums
            </CantileverButton>
          </Form.Item>
        </StyledForm>
      </div>
    </div>
  )
}

export const SectionAddendumsSection: ReportSection = {
  id: RugReportSectionId.SECTION_ADDENDUMS,
  title: 'Section Addendums',
  description: 'Add blank pages to existing or new sections',
  required: false,
  RenderReact,
  RenderPDF: () => null,
  requiredFields: [],
  showInTOC: false,
}
